import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tooltip as WmsTooltip } from "react-tooltip";
import { getAllTaskPriority, getAllTaskStatus, getAllTaskType, createTask, getTaskById, updateTaskAttachments, updateTask, handleDownloadFile, handleDownloadAllFiles, startWorkmanTimeLog, endWorkmanTimeLog } from '../../store/actions/picker.action';
import { getEffectiveRoleName, isDefaultSuperAdminRole, isSuperAdminRole } from '../../constants/roleConstants';
import { fetchCompanyFilters, getFilterSellers } from '../../store/actions/rateCard';
import { getAllSellersList } from '../../store/actions/company';
import { GetAllLedgerCurrency, GetAllLedgerPaymentMode } from '../../store/actions/accounts.action';
import Select from 'react-select';
import { toast } from "react-toastify";
import { allowedDocExtensions } from '../../constant';
import _ from 'lodash';
import moment from 'moment';
import ChargeSeller from './ChargeSeller';
import CommissionSection, {
    applyCommissionPayload,
    calculateCommissionAmount,
    getChargeDefaultAmount,
    getCommissionErrors,
    getCommissionPostIssue,
    isAllowedCommissionBaseInput,
    isAllowedCommissionRateInput,
    isCommissionTaskTypeSelected,
} from './Commission';
import { hasUnsavedTaskChanges, persistTaskBeforeCharge, toTaskSnapshot, uploadNewTaskAttachments } from './taskChanges';
import { findConfiguration, getSelectList, computeAutoSelectedSeller } from '../../config/utils';
import PdfMergePopup from '../common/PDFMerge';
import LoaderSpinner from '../common/Loader';
import DatePickerComponent from '../common/DatePickerComponent';
import InlineFilePreview from '../common/PreviewAttachment';
import ViewImage from '../common/ViewImage';
import ChargesHistory from '../CarrierConnect/ChargesHistory';
import { Tooltip } from 'react-tooltip';
import AlertDialogModal from '../common/AlertDialog';
import TopUpModal from '../modal/TopUpModal';
import TopUpIconWhite from '../../assets/topUpWhite.svg';

const isOpenWorkmanTimeLog = (log) => Boolean(log?.startDateTime) && !log?.endDateTime;

const deriveTaskViewFlags = ({
    formData,
    id,
    isEditable,
    isWorkmanLaborType,
    userDetailsValue,
}) => {
    const hasWorkmanDataFromServer = Array.isArray(formData.workmanTimeLogs) && formData.workmanTimeLogs.length > 0;
    const workmanLogs = formData.workmanTimeLogs || [];

    const taskCategory = formData?.taskCategory;
    const isReturnContextTask = taskCategory === 'ReturnPackage' || taskCategory === 'ReturnProduct';
    const isExistingTask = Number(id) > 0;

    const userRoleName = getEffectiveRoleName(userDetailsValue);
    const isSuperAdminUser = isSuperAdminRole(userDetailsValue);
    const isDefaultSuperAdminUser = isDefaultSuperAdminRole(userDetailsValue);
    const isWarehouseUser = userRoleName === 'Warehouse Manager' || userRoleName === 'Warehouse Worker';
    const canViewWorkmanSection = isSuperAdminUser || isWarehouseUser;

    const isWorkmanContext = isWorkmanLaborType || (isReturnContextTask && hasWorkmanDataFromServer);
    const showWorkmanSection = isExistingTask && canViewWorkmanSection && isWorkmanContext;
    const isWorkmanEditable = isWorkmanLaborType && isEditable;
    const canEditWorkmanInputs = isWorkmanEditable && !isWarehouseUser;
    const canPerformWorkmanActions = isWarehouseUser || isSuperAdminUser;
    const showWorkmanActionButtons = isWorkmanLaborType && isExistingTask && canPerformWorkmanActions;

    const openWorkmanLogIndex = workmanLogs.findIndex(isOpenWorkmanTimeLog);
    const hasOpenWorkmanLog = openWorkmanLogIndex >= 0;

    return {
        hasWorkmanDataFromServer,
        isReturnContextTask,
        isExistingTask,
        userRoleName,
        isSuperAdminUser,
        isDefaultSuperAdminUser,
        isWarehouseUser,
        canViewWorkmanSection,
        showWorkmanSection,
        isWorkmanEditable,
        canEditWorkmanInputs,
        showWorkmanActionButtons,
        openWorkmanLogIndex,
        hasOpenWorkmanLog,
    };
};

/*istanbul ignore next */
const Tasks = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    let dispatch = useDispatch();
    const location = useLocation();
    const userDetails = useSelector((state) => state.auth.user);
    const [isEditable, setIsEditable] = useState(location?.state?.editable);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageRecord, setSelectedImageRecord] = useState({});
    const configurations = useSelector((state) => state?.auth?.configurations);
    const [maxSize, setMaxSize] = useState(0);
    const [showLoader, setShowLoader] = useState(false);
    const [showPopUp, setShowPopUp] = useState(false);
    const [formReset, setFormReset] = useState([]);
    const [showCharge, setShowCharge] = useState(false);
    const [showChargeAlert, setShowChargeAlert] = useState(false);
    const [showTopUpModal, setShowTopUpModal] = useState(false);
    const [formData, setFormData] = useState({
        taskTypeId: 0,
        taskPriorityId: 0,
        taskTitle: '',
        taskStatusId: 1,
        sku: '',
        quantity: '',
        instructions: '',
        attachments: [],
        amount: 0,
        baseCurrencyId: 1,
        conversionRate: 1,
        baseConversionAmount: '',
        paymentModeId: '',
        companyId: 0,
        sellerId: 0,
        entryDate: new Date(),
        commissionBaseAmount: '',
        commissionRate: '',
        internalNotes: '',
        actualCost: '',
        // Workman Time Log entries — each entry: { id?, startDateTime, endDateTime }
        workmanTimeLogs: [],
    });
    const [taskSnapshot, setTaskSnapshot] = useState(null);
    const [errors, setErrors] = useState({});
    const [showChargesDetails, setShowChargesDetails] = useState(false);
    const [companyList, setCompanyList] = useState([]);
    const [sellerList, setSellerList] = useState([]);
    const { allTaskPriorityList, allTaskStatusList, allTaskTypeList } = useSelector((state) => state.pickerReducer);
    const { ledgerCurrencyList = [], ledgerPaymentModeList = [] } = useSelector((state) => state.accountingReducer || {});
    const { filterCompanies, filterAllSellers } = useSelector((state) => state.RateCardReducer);
    const MAX_FILE_COUNT = 10;

    // Resolve the "Workman Labor" task type id by name (case-insensitive contains)
    const workmanTaskTypeId = useMemo(() => {
        return allTaskTypeList?.find((t) => t?.typeName?.toLowerCase?.().includes('workman labor'))?.id;
    }, [allTaskTypeList]);

    const walletRechargeTaskTypeId = useMemo(() => {
        return allTaskTypeList?.find((taskType) => taskType?.typeName?.toLowerCase?.() === 'wallet recharge')?.id;
    }, [allTaskTypeList]);

    const closedTaskStatusId = useMemo(() => {
        return allTaskStatusList?.find((status) => status?.statusName?.toLowerCase?.() === 'closed')?.id;
    }, [allTaskStatusList]);

    const isWorkmanLaborType = Boolean(
        workmanTaskTypeId !== undefined && Number(formData.taskTypeId) === Number(workmanTaskTypeId)
    );
    const isWalletRechargeTaskType = walletRechargeTaskTypeId !== undefined
        ? Number(formData.taskTypeId) === Number(walletRechargeTaskTypeId)
        : Number(formData.taskTypeId) === 4;

    const isCommissionTaskType = isCommissionTaskTypeSelected(allTaskTypeList, formData.taskTypeId);
    const commissionAmount = calculateCommissionAmount(formData.commissionBaseAmount, formData.commissionRate);

    const calculateWalletRechargeAmount = (baseConversionAmount, conversionRate, baseCurrencyId) => {
        const conversionAmountValue = Number(baseConversionAmount);
        const conversionRateValue = Number(conversionRate);
        if (!conversionAmountValue || !conversionRateValue) return '';

        return Number(baseCurrencyId) === 1
            ? conversionAmountValue.toString()
            : (conversionAmountValue / conversionRateValue).toFixed(2);
    };

    const renderConversionAmountDisplay = () => {
        const calculatedAmount = calculateWalletRechargeAmount(
            formData.baseConversionAmount,
            formData.conversionRate,
            formData.baseCurrencyId
        );
        if (!calculatedAmount) return null;

        return (
            <span className="mt-1 d-block">
                {formData.baseConversionAmount} / {formData.conversionRate} = $ {calculatedAmount}
            </span>
        );
    };

    // Derived flags for Workman Labor / role-based visibility; extracted to keep Tasks below cognitive-complexity limit.
    const {
        isSuperAdminUser,
        isDefaultSuperAdminUser,
        isWarehouseUser,
        showWorkmanSection,
        canEditWorkmanInputs,
        showWorkmanActionButtons,
        hasOpenWorkmanLog,
    } = deriveTaskViewFlags({
        formData,
        id,
        isEditable,
        isWorkmanLaborType,
        userDetailsValue: userDetails?.Value,
    });

    const computeDurationMinutes = (start, end) => {
        if (!start || !end) return 0;
        const startM = moment(start).startOf('minute');
        const endM = moment(end).startOf('minute');
        if (!startM.isValid() || !endM.isValid() || endM.isSameOrBefore(startM)) return 0;
        return Math.round(endM.diff(startM, 'minutes', true));
    };

    const formatDurationLabel = (minutes) => {
        if (!minutes || minutes <= 0) return '0h 0m';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
    };

    const computeDurationLabel = (start, end) => {
        const mins = computeDurationMinutes(start, end);
        return mins > 0 ? formatDurationLabel(mins) : '';
    };

    const totalWorkmanMinutes = useMemo(() => {
        if (!Array.isArray(formData.workmanTimeLogs)) return 0;
        return formData.workmanTimeLogs.reduce(
            (sum, log) => sum + computeDurationMinutes(log?.startDateTime, log?.endDateTime),
            0
        );
    }, [formData.workmanTimeLogs]);

    const getRowRangeError = (log) => {
        if (!log?.startDateTime || !log?.endDateTime) return '';
        return moment(log.endDateTime).isSameOrBefore(moment(log.startDateTime))
            ? 'End datetime must be after start datetime'
            : '';
    };

    const handleWorkmanLogChange = (index, field, value) => {
        setFormData((prev) => {
            const logs = Array.isArray(prev.workmanTimeLogs) ? [...prev.workmanTimeLogs] : [];
            logs[index] = { ...logs[index], [field]: value };
            return { ...prev, workmanTimeLogs: logs };
        });
        // Clear field-specific error for this row when user edits
        setErrors((prev) => {
            if (!prev?.workmanTimeLogs?.[index]?.[field]) return prev;
            const next = { ...prev, workmanTimeLogs: [...prev.workmanTimeLogs] };
            next.workmanTimeLogs[index] = { ...next.workmanTimeLogs[index] };
            delete next.workmanTimeLogs[index][field];
            return next;
        });
    };

    const handleAddWorkmanLog = () => {
        setFormData((prev) => ({
            ...prev,
            workmanTimeLogs: [
                ...(Array.isArray(prev.workmanTimeLogs) ? prev.workmanTimeLogs : []),
                { startDateTime: null, endDateTime: null },
            ],
        }));
    };

    const handleRemoveWorkmanLog = (index) => {
        setFormData((prev) => {
            const logs = Array.isArray(prev.workmanTimeLogs) ? [...prev.workmanTimeLogs] : [];
            logs.splice(index, 1);
            return { ...prev, workmanTimeLogs: logs };
        });
        setErrors((prev) => {
            if (!prev?.workmanTimeLogs) return prev;
            const arr = [...prev.workmanTimeLogs];
            arr.splice(index, 1);
            return { ...prev, workmanTimeLogs: arr };
        });
    };

    // Ensure at least one editable log row exists when Workman Labor type is active in edit mode
    // (only for users who can edit inputs manually — warehouse users use Start/End Task buttons).
    // Skipped on the Create Task page since the section is hidden there and an empty row would
    // serialize to {null,null} causing a backend DateTime parse error.
    useEffect(() => {
        if (canEditWorkmanInputs && Number(id) > 0) {
            const logs = Array.isArray(formData.workmanTimeLogs) ? formData.workmanTimeLogs : [];
            if (logs.length === 0) {
                setFormData((prev) => ({
                    ...prev,
                    workmanTimeLogs: [{ startDateTime: null, endDateTime: null }],
                }));
            }
        }
    }, [canEditWorkmanInputs, formData.workmanTimeLogs]);

    const handleStartWorkmanTask = async () => {
        if (!Number(id)) return;
        try {
            setShowLoader(true);
            const res = await startWorkmanTimeLog({ taskId: Number(id) });
            setShowLoader(false);
            if (res?.data?.Status === 200) {
                toast.success('Task started');
                // Preserve current edit mode so the form doesn't flip to view-only
                // after the refetch (server's isEditable may differ from the
                // user's current edit-toggle state).
                fetchTaskDetails(true);
            } else {
                toast.error(res?.data?.Message || 'Failed to start task');
            }
        } catch (err) {
            setShowLoader(false);
            console.error('Start workman task failed:', err);
            toast.error('Failed to start task');
        }
    };

    const handleEndWorkmanTask = async () => {
        if (!Number(id)) return;
        try {
            setShowLoader(true);
            const res = await endWorkmanTimeLog({ taskId: Number(id) });
            setShowLoader(false);
            if (res?.data?.Status === 200) {
                toast.success('Task ended');
                fetchTaskDetails(true);
            } else {
                toast.error(res?.data?.Message || 'Failed to end task');
            }
        } catch (err) {
            setShowLoader(false);
            console.error('End workman task failed:', err);
            toast.error('Failed to end task');
        }
    };

    useEffect(() => {
        if (configurations && configurations.length > 0) {
            const maxSizeConfig = findConfiguration('TASK_ATTACHMENT_SIZE', configurations);
            if (maxSizeConfig) {
                setMaxSize(maxSizeConfig);
            }
        }
    }, [configurations]);

    useEffect(() => {
        if (Number(id) > 0) {
            fetchTaskDetails();
        }
    }, [id]);

    // Fetches sellers for the given company (mirrors CarrierConnect's fetchSellers).
    const fetchSellers = (companyId) => {
        if (!companyId) {
            setSellerList([]);
            return;
        }
        getAllSellersList({ companyIds: [companyId] })
            .then((res) => {
                const list = res?.data?.Entity?.listSellerMasterOrdersEntity;
                if (res?.data?.Status === 200 && list?.length) {
                    const mapped = list.map((item) => ({
                        ...item,
                        label: item.sellerAndCompany,
                        value: item.id,
                    }));
                    setSellerList(mapped);
                    const lone = computeAutoSelectedSeller(mapped);
                    if (lone) {
                        setFormData((prev) => (Number(prev?.sellerId) ? prev : { ...prev, sellerId: lone.value }));
                    }
                } else {
                    setSellerList([]);
                }
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.warn('Tasks: seller refresh failed', error?.message);
                setSellerList([]);
            });
    };

    useEffect(() => {
        if (!allTaskPriorityList?.length) {
            dispatch(getAllTaskPriority());
        }

        if (!allTaskStatusList?.length) {
            dispatch(getAllTaskStatus());
        }

        if (!allTaskTypeList?.length) {
            dispatch(getAllTaskType());
        }

        if (!ledgerCurrencyList?.length) {
            dispatch(GetAllLedgerCurrency());
        }

        if (!ledgerPaymentModeList?.length) {
            dispatch(GetAllLedgerPaymentMode());
        }
    }, []);

    // Fetch companies + initial (all) sellers for super admins — same actions as CarrierConnect.
    useEffect(() => {
        if (isSuperAdminUser) {
            const companyPayload = {
                pageIndex: 1,
                pageSize: 100,
                sortColumn: "name",
                sortOrder: "ascending",
            };
            const sellerPayload = {
                pageIndex: 1,
                pageSize: 100,
                sortColumn: "name",
                sortOrder: "ASCENDING",
                companyIdList: [0],
                screenType: 2,
            };
            dispatch(fetchCompanyFilters(companyPayload));
            dispatch(getFilterSellers(sellerPayload));
        }
    }, [isSuperAdminUser]);

    // Re-scope the seller list to the currently selected company (handles both
    // create flow and re-hydration after an existing task is loaded).
    useEffect(() => {
        if (formData.companyId && isSuperAdminUser) {
            fetchSellers(formData.companyId);
        } else if (!formData.companyId) {
            setSellerList([]);
        }
    }, [formData.companyId]);

    // Populate company and initial seller lists from Redux state.
    useEffect(() => {
        const companyData = filterCompanies?.Entity?.listCompanyMasterOrdersEntity || [];
        if (companyData.length) {
            setCompanyList(getSelectList(companyData, 'Id', 'name'));
        }
        const sellerData = filterAllSellers?.EntityList || [];
        if (sellerData.length && !formData.companyId) {
            setSellerList(getSelectList(sellerData, 'id', 'sellerName'));
        }
    }, [filterCompanies, filterAllSellers]);

    // Validation helpers extracted to reduce cognitive complexity
    const isNonZero = (v) => Number(v) && Number(v) !== 0;
    const isNonEmptyString = (v) => typeof v === 'string' && v.trim() !== '';

    const validateAmountForTaskType = (formData, errors) => {
        if (isWalletRechargeTaskType && (!formData.amount || Number(formData.amount) <= 0)) {
            errors.amount = 'Amount is required for this task type';
        }

        if (!isWalletRechargeTaskType) return;

        if (!formData.baseCurrencyId || Number(formData.baseCurrencyId) === 0) {
            errors.baseCurrencyId = 'Currency is required';
        }
        if (!formData.conversionRate || Number(formData.conversionRate) <= 0) {
            errors.conversionRate = 'Conversion Rate is required';
        }
        if (!formData.baseConversionAmount || Number(formData.baseConversionAmount) <= 0) {
            errors.baseConversionAmount = 'Conversion Amount is required';
        }
        if (!formData.paymentModeId || Number(formData.paymentModeId) === 0) {
            errors.paymentModeId = 'Payment Mode is required';
        }
    };

    const validateCompanySellerForSuperAdmin = (formData, errors) => {
        const isSuperAdminCreate = isSuperAdminUser && Number(id) === 0;
        if (!isSuperAdminCreate) return;

        if (!formData.companyId || Number(formData.companyId) === 0) {
            errors.companyId = 'Company is required';
        }
        if (!formData.sellerId || Number(formData.sellerId) === 0) {
            errors.sellerId = 'Seller is required';
        }
    };

    const validateForm = (formData) => {
        const errors = {};
        // Required field checks
        const requiredChecks = [
            { key: 'taskTypeId', ok: isNonZero, msg: 'Task Type is required' },
            { key: 'taskPriorityId', ok: isNonZero, msg: 'Task Priority is required' },
            { key: 'taskTitle', ok: isNonEmptyString, msg: 'Task Title is required' },
            { key: 'taskStatusId', ok: isNonZero, msg: 'Task Status is required' },
        ];

        requiredChecks.forEach(({ key, ok, msg }) => {
            if (!ok(formData[key])) {
                errors[key] = msg;
            }
        });

        // Conditional validations
        validateAmountForTaskType(formData, errors);
        validateCompanySellerForSuperAdmin(formData, errors);
        Object.assign(errors, getCommissionErrors(formData, isCommissionTaskType));

        // Workman Labor — at least one time log row with valid start/end (end > start).
        // Only validated on existing tasks: the section is hidden on Create, and
        // warehouse users track time via the Start/End Task buttons after save
        // rather than seeding rows manually.
        if (isWorkmanLaborType && Number(id) > 0 && !isWarehouseUser) {
            const logs = Array.isArray(formData.workmanTimeLogs) ? formData.workmanTimeLogs : [];
            if (logs.length === 0) {
                errors.workmanTimeLogs = [{ startDateTime: 'Start datetime is required', endDateTime: 'End datetime is required' }];
            } else {
                const rowErrors = [];
                let hasAnyRowError = false;
                logs.forEach((log, idx) => {
                    const rowErr = {};
                    if (!log?.startDateTime) {
                        rowErr.startDateTime = 'Start datetime is required';
                    }
                    if (!log?.endDateTime) {
                        rowErr.endDateTime = 'End datetime is required';
                    }
                    const rangeErr = getRowRangeError(log);
                    if (rangeErr) {
                        rowErr.endDateTime = rangeErr;
                    }
                    rowErrors[idx] = rowErr;
                    if (Object.keys(rowErr).length > 0) hasAnyRowError = true;
                });
                if (hasAnyRowError) {
                    errors.workmanTimeLogs = rowErrors;
                }
            }
        }

        setErrors(errors);
        return errors;
    };

    // Saves pending edits (fields, new and removed attachments) so the charge matches what is on screen.
    const saveChangesBeforeCharge = async () => {
        if (Object.keys(validateForm(formData)).length > 0) return false;
        const loaderAndToast = { setLoader: setShowLoader, onFailure: toast.error };
        const isSaved = await persistTaskBeforeCharge(updateTask, prepareTaskData(), loaderAndToast)
            && await uploadNewTaskAttachments(updateTaskAttachments, id, formData.attachments, loaderAndToast);
        if (!isSaved) return false;
        await fetchTaskDetails(true);
        toast.success('Changes saved.');
        return true;
    };

    const openChargeSellerDialog = async () => {
        const commissionIssue = getCommissionPostIssue(isCommissionTaskType, formData);
        if (commissionIssue) {
            setErrors({ ...errors, ...commissionIssue.errors });
            toast.info(commissionIssue.message);
            return;
        }
        if (hasUnsavedTaskChanges(formData, taskSnapshot) && !(await saveChangesBeforeCharge())) return;
        if (formData?.postCharges?.length > 0) {
            setShowChargeAlert(true);
        } else {
            setShowCharge(!showCharge);
        }
    };

    // Helper function to prepare task data for submission
    const prepareTaskData = () => {
        const applyWalletRechargeFields = (payload) => {
            if (!isWalletRechargeTaskType) {
                return {
                    ...payload,
                    baseCurrencyId: null,
                    conversionRate: null,
                    baseConversionAmount: null,
                    paymentModeId: null,
                };
            }

            return {
                ...payload,
                baseCurrencyId: payload.baseCurrencyId ? Number(payload.baseCurrencyId) : null,
                conversionRate: payload.conversionRate ? Number(payload.conversionRate) : null,
                baseConversionAmount: payload.baseConversionAmount ? Number(payload.baseConversionAmount) : null,
                paymentModeId: payload.paymentModeId ? Number(payload.paymentModeId) : null,
            };
        };

        const applyWorkmanFields = (payload, { isUpdate }) => {
            // Backend expects workmanTimeLogs as an array of { id?, startDateTime, endDateTime }.
            if (!isWorkmanLaborType) {
                // Clear when type isn't Workman Labor so stale values don't persist
                return { ...payload, workmanTimeLogs: [] };
            }
            // On update, workman time logs are managed by dedicated endpoints
            // (Start/End Task buttons and the in-card Save). Re-sending them via
            // the Update Task API causes duplicate rows to be appended, so strip
            // the field from the update payload.
            if (isUpdate) {
                const rest = { ...payload };
                delete rest.workmanTimeLogs;
                return rest;
            }
            const logs = Array.isArray(payload.workmanTimeLogs) ? payload.workmanTimeLogs : [];
            // Drop empty/placeholder rows — backend rejects null DateTime values.
            const serialized = logs
                .filter((log) => log?.startDateTime && log?.endDateTime)
                .map((log) => ({
                    ...(log?.id ? { id: log.id } : {}),
                    startDateTime: moment(log.startDateTime).format('YYYY-MM-DDTHH:mm:ss'),
                    endDateTime: moment(log.endDateTime).format('YYYY-MM-DDTHH:mm:ss'),
                }));
            return {
                ...payload,
                workmanTimeLogs: serialized,
            };
        };

        const applyInternalFields = (payload) => {
            if (!isDefaultSuperAdminUser) {
                return { ...payload, internalNotes: null, actualCost: null };
            }
            const notes = String(payload.internalNotes ?? '').trim();
            const hasCost = String(payload.actualCost ?? '').trim() !== '';
            return {
                ...payload,
                internalNotes: notes || null,
                actualCost: hasCost ? Number(payload.actualCost) : null,
            };
        };

        if (Number(id) > 0) {
            return applyInternalFields(applyWorkmanFields(applyCommissionPayload(applyWalletRechargeFields({ ...formData, isActive: true }), isCommissionTaskType, commissionAmount), { isUpdate: true }));
        }

        const baseData = { ...formData };
        if (isSuperAdminUser) {
            baseData.taskForCompanyId = formData.companyId;
            baseData.taskForSellerId = formData.sellerId;
        }
        return applyInternalFields(applyWorkmanFields(applyCommissionPayload(applyWalletRechargeFields(baseData), isCommissionTaskType, commissionAmount), { isUpdate: false }));
    };

    // Helper function to submit task (create or update)
    const submitTaskData = async (taskData) => {
        const isUpdate = Number(id) > 0;
        return isUpdate ? updateTask(taskData) : createTask(taskData);
    };

    // Helper function to handle success response
    const handleTaskSuccess = (response) => {
        const newAttachments = formData.attachments.filter(file => file.isNew);
        const hasNewAttachments = newAttachments.length > 0;

        if (hasNewAttachments) {
            const taskId = Number(id) > 0 ? id : response.data.Entity;
            handleSaveAttachments(taskId);
            return;
        }

        setShowLoader(false);
        const isUpdate = Number(id) > 0;
        const message = isUpdate ? 'Task Updated successfully!' : 'Task created successfully!';
        toast.success(message);
        navigate('/tasks');
    };

    // Helper function to extract error message from response
    const getErrorMessage = (response) => {
        return response?.data?.Message?.AppStatusDescription ||
            response?.data?.Message ||
            'Failed to create/update task. Please try again.';
    };

    // Helper function to check if response indicates success
    const isSuccessResponse = (response) => {
        return response?.data?.Status === 200;
    };

    const handleSaveTask = async () => {
        const errors = validateForm(formData);
        if (Object.keys(errors).length > 0) {
            console.log('Validation Errors:', errors);
            return;
        }

        try {
            setShowLoader(true);
            const taskData = prepareTaskData();
            const response = await submitTaskData(taskData);

            if (isSuccessResponse(response)) {
                handleTaskSuccess(response);
            } else {
                setShowLoader(false);
                if (!response?.data?._readOnlyBlocked) {
                    const apiErrorMessage = getErrorMessage(response);
                    toast.error(apiErrorMessage);
                }
            }
        } catch (error) {
            setShowLoader(false);
            if (!error?.response?.data?._readOnlyBlocked) {
                toast.error('An error occurred while creating the task.');
            }
        }
    };

    const handleOpenTopUpModal = async () => {
        const errors = validateForm(formData);
        if (Object.keys(errors).length > 0) {
            console.log('Validation Errors:', errors);
            return;
        }

        try {
            setShowLoader(true);
            const response = await updateTask(prepareTaskData());
            setShowLoader(false);

            if (isSuccessResponse(response)) {
                setShowTopUpModal(true);
                return;
            }

            if (!response?.data?._readOnlyBlocked) {
                toast.error(getErrorMessage(response));
            }
        } catch (error) {
            setShowLoader(false);
            if (!error?.response?.data?._readOnlyBlocked) {
                toast.error('An error occurred while updating the task.');
            }
        }
    };

    const handleCancelPdfPopUp = () => {
        setShowPopUp(!showPopUp);
    };

    const handleUploadPdfAction = (file, fileDetail) => {
        handleCancelPdfPopUp();
    }

    const handleSaveAttachments = async (taskId) => {
        const formDataToSend = new FormData();
        let newFiles = formData.attachments = formData.attachments.filter(file => file.isNew);
        newFiles.forEach(file => {
            formDataToSend.append('files', file);
        });
        formDataToSend.append('TaskId', taskId);

        try {
            let response = await updateTaskAttachments(formDataToSend);
            if (response.status === 200) {
                setShowLoader(false);
                toast.success(Number(id) > 0 ? 'Task Updated successfully!' : 'Task created successfully!');
                navigate('/tasks');
            } else {
                setShowLoader(false);
                toast.error('Failed to save attachments. Please try again.');
            }
        } catch (error) {
            setShowLoader(false);
            toast.error('An error occurred while saving attachments.');
        }
    }

    const handleResetForm = () => {
        if (Number(id) > 0) {
            fetchTaskDetails();
        } else {
            setFormData({
                taskTypeId: 0,
                taskPriorityId: 0,
                taskTitle: '',
                taskStatusId: 0,
                sku: '',
                quantity: '',
                instructions: '',
                attachments: [],
                amount: 0,
                baseCurrencyId: 1,
                conversionRate: 1,
                baseConversionAmount: '',
                paymentModeId: '',
                companyId: 0,
                sellerId: 0,
                commissionBaseAmount: '',
                commissionRate: '',
                internalNotes: '',
                actualCost: '',
                workmanTimeLogs: [],
            });
        }
        setFormReset(true);
    };

    const handleCommissionChange = (field, isAllowed) => (e) => {
        const { value } = e.target;
        if (!isAllowed(value)) return;
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: '' }));
        setFormReset(false);
    };

    const handleActualCostChange = (e) => {
        const { value } = e.target;
        if (!/^\d{0,10}(\.\d{0,2})?$/.test(value)) return;
        setFormData((prev) => ({ ...prev, actualCost: value }));
        setFormReset(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'baseCurrencyId') {
            const conversionRate = Number(value) === 1 ? 1 : '';
            setFormData({
                ...formData,
                baseCurrencyId: value,
                conversionRate,
                amount: calculateWalletRechargeAmount(formData.baseConversionAmount, conversionRate, value),
            });
            setErrors({
                ...errors,
                baseCurrencyId: '',
                conversionRate: '',
            });
            setFormReset(false);
            return;
        }

        if (name === 'baseConversionAmount' && isWalletRechargeTaskType) {
            setFormData({
                ...formData,
                baseConversionAmount: value,
                amount: calculateWalletRechargeAmount(value, formData.conversionRate, formData.baseCurrencyId),
            });
            setErrors({
                ...errors,
                baseConversionAmount: '',
                amount: '',
            });
            setFormReset(false);
            return;
        }

        if (name === 'conversionRate' && isWalletRechargeTaskType) {
            setFormData({
                ...formData,
                conversionRate: value,
                amount: calculateWalletRechargeAmount(formData.baseConversionAmount, value, formData.baseCurrencyId),
            });
            setErrors({
                ...errors,
                conversionRate: '',
                amount: '',
            });
            setFormReset(false);
            return;
        }

        setFormData({
            ...formData,
            [name]: value,
        });
        setErrors({
            ...errors,
            [name]: '', // Clear error for the field being edited
        });
        setFormReset(false);
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value.trim(),
        });
    }

    const getTaskCompanyOption = () => companyList?.find((company) => Number(company.value) === Number(formData.companyId)) || {
        Id: formData.companyId,
        value: formData.companyId,
        label: formData.companyName || formData.company || 'Selected Company',
        name: formData.companyName || formData.company || 'Selected Company',
    };

    const getTaskSellerOption = () => sellerList?.find((seller) => Number(seller.value) === Number(formData.sellerId)) || {
        value: formData.sellerId,
        label: formData.sellerName || formData.sellerAndCompany || 'Selected Seller',
    };

    const getTaskTopUpData = () => {
        // Default the Top Up modal's Transaction Date to the task's entry (creation) date shown on the edit screen.
        const entryDateMoment = formData.entryDate ? moment(formData.entryDate) : null;
        const createdDate = entryDateMoment?.isValid()
            ? entryDateMoment.format('MM-DD-YYYY')
            : '';
        return {
            taskId: Number(id) || 0,
            topupCompanyId: getTaskCompanyOption(),
            topupSellerId: getTaskSellerOption(),
            amount: formData.amount || 0,
            baseCurrencyId: formData.baseCurrencyId || 1,
            conversionRate: formData.conversionRate || 1,
            baseConversionAmount: formData.baseConversionAmount || formData.amount || '',
            paymentModeId: formData.paymentModeId || '',
            createdDate,
        };
    };

    const handleTaskTopUpComplete = async () => {
        if (!Number(id) || !closedTaskStatusId) return;

        setFormData((prev) => ({
            ...prev,
            taskStatusId: closedTaskStatusId,
        }));
    };

    const renderTaskTopUpModal = () => showTopUpModal && (
        <TopUpModal
            show={showTopUpModal}
            showModal={showTopUpModal}
            activeSellerList={companyList?.length ? companyList : [getTaskCompanyOption()]}
            onHide={() => setShowTopUpModal(false)}
            fundList={[]}
            filterData={{}}
            showConversionFields
            onTopUpComplete={handleTaskTopUpComplete}
            initialTopUpData={getTaskTopUpData()}
        />
    );

    // Normalize workman time logs from server response (supports legacy single start/end fields)
    const normalizeWorkmanLogsFromServer = (entity) => {
        const serverLogs = Array.isArray(entity?.workmanTimeLogs) ? entity.workmanTimeLogs : [];
        if (serverLogs.length > 0) {
            return serverLogs.map((log) => ({
                id: log?.id,
                startDateTime: log?.startDateTime ? new Date(log.startDateTime) : null,
                endDateTime: log?.endDateTime ? new Date(log.endDateTime) : null,
            }));
        }
        if (entity?.workmanStartDateTime || entity?.workmanEndDateTime) {
            return [{
                startDateTime: entity.workmanStartDateTime ? new Date(entity.workmanStartDateTime) : null,
                endDateTime: entity.workmanEndDateTime ? new Date(entity.workmanEndDateTime) : null,
            }];
        }
        return [];
    };

    const fetchTaskDetails = async (preserveEditable = false) => {
        setShowLoader(true);
        let res = await getTaskById(id);
        if (res.data.Status === 200) {
            if (!preserveEditable) {
                setIsEditable(!location?.state?.editable ? false : res.data.Entity.isEditable);
            }
            setShowLoader(false);
            const stateData = location?.state?.data || {};
            const refType = res.data.Entity.taskReferenceType;
            let derivedCategory;
            if (refType === 'Package') derivedCategory = 'ReturnPackage';
            else if (refType === 'Product') derivedCategory = 'ReturnProduct';
            else if (refType === 'General') derivedCategory = 'General';
            let obj = {
                ...res.data.Entity,
                taskId: res.data.Entity.id,
                taskCategory:
                    derivedCategory || res.data.Entity.taskCategory || stateData.taskCategory,
                packageTrackingNumber:
                    res.data.Entity.packageTrackingNumber || stateData.packageTrackingNumber,
                workmanTimeLogs: normalizeWorkmanLogsFromServer(res.data.Entity),
            }
            setFormData(obj);
            setTaskSnapshot(toTaskSnapshot(obj));
        } else {
            setShowLoader(false);
            console.log('Error Log:', res.data);
        }
    };

    const handleDeleteAttachment = (item, index) => {
        let formDataClone = _.cloneDeep(formData);
        if (item.isNew) {
            formDataClone.attachments.splice(index, 1);
        } else {
            formDataClone.attachments[index].isDeleted = true;
            formDataClone.attachmentActions = formDataClone.attachmentActions ?? [];
            formDataClone.attachmentActions.push(formDataClone.attachments[index]);
            formDataClone.attachments.splice(index, 1);
        }
        setFormData(formDataClone);
    };

    const handleDownload = async (item) => {
        let response = await handleDownloadFile(encodeURIComponent(item.fileUrl));
        if (response.status === 200) {
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', item.name);
            document.body.appendChild(link); // NOSONAR
            link.click();
            document.body.removeChild(link); // NOSONAR
        } else {
            toast.error('Failed to download file.');
        }
    }

    const handleAttachments = (e) => {
        let files = [...e.target.files];
        try {
            if (files.length === 0) return;
            if (files.length > MAX_FILE_COUNT) {
                toast.info(`Only ${MAX_FILE_COUNT} files can be uploaded at one time`, 1000);
                e.target.value = "";
                return;
            }

            // Validate extensions and individual file size
            let validFiles = [];
            let totalSizeBytes = 0;

            files.forEach((file) => {
                const extension = file.name.split(".").pop()?.toLowerCase();
                const fileSizeBytes = file.size;

                if (!allowedDocExtensions.includes(extension)) {
                    toast.error(`Unsupported file format: ${file.name}`, 1000);
                    return;
                }
                totalSizeBytes += fileSizeBytes;
                file.isNew = true;
                validFiles.push(file);
            });

            // Check total size
            if (totalSizeBytes > maxSize * 1024 * 1024) {
                toast.info(`Total file size should not exceed ${maxSize}MB`, 1000);
                e.target.value = "";
                return;
            }

            if (validFiles.length > 0) {
                setFormData((prev) => ({
                    ...prev,
                    attachments: [...prev.attachments, ...validFiles],
                }));
            }

            e.target.value = ""; // Reset input
        } catch (err) {
            console.log(err);
        }
    };

    const handleEditRecord = () => {
        if (formData.isEditable) {
            setIsEditable(!isEditable);
        };
    };

    const handleDownloadAll = async () => {
        let oldAttachments = formData.attachments.filter(file => !file.isNew);
        let response = await handleDownloadAllFiles(oldAttachments);
        if (response.status === 200) {
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const disposition = response.headers['content-disposition'];
            let filename = `Attachments_${new Date().toISOString()}.zip`;
            if (disposition && disposition.indexOf('filename=') !== -1) {
                filename = disposition
                    .split('filename=')[1]
                    .split(';')[0]
                    .replace(/"/g, ''); //NOSONAR
            }
            link.setAttribute('download', filename);
            document.body.appendChild(link); // NOSONAR
            link.click();
            document.body.removeChild(link); // NOSONAR
        } else {
            toast.error('Failed to download file.');
        }
    };

    const handleDateFieldChange = (date) => {
        setFormData({
            ...formData,
            entryDate: date
        });
    };

    const handleViewImage = (item) => {
        let data = {
            ...item,
            imageUrl: item.fileUrl
        }
        setSelectedImageRecord(data);
        setShowImageModal(true);
    };

    const handleChargesDetails = () => {
        setShowChargesDetails(!showChargesDetails);
    };

    const handleOverrideCharge = () => {
        setShowCharge(!showCharge);
        setShowChargeAlert(false);
    };

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    <span>{Number(id) ? 'Update' : 'Create'} {(() => {
                        const category = formData?.taskCategory || location?.state?.data?.taskCategory;
                        if (category === 'ReturnPackage') return 'Return Package Task';
                        if (category === 'ReturnProduct') return 'Return Product Task';
                        return 'Task';
                    })()} {Number(id) > 0 && `(# ${Number(id)})`} {formData?.id && formData?.postCharges?.length > 0 && <button id='chargesBtn' className='sortBtn fontFix' onClick={handleChargesDetails}><i className='fa fa-info-circle' aria-hidden="true" style={{ color: "#ff0000" }}></i></button>}</span>

                    <div className="top_right_button_container">
                        <div className="top_right_button_container">
                            {isDefaultSuperAdminUser && Number(id) > 0 && (
                                <button
                                    className="btn cus-seconday-bg-btn top-btn"
                                    onClick={openChargeSellerDialog}
                                >
                                    Post Charges
                                </button>
                            )}
                            <button
                                className="btn cus-seconday-bg-btn top-btn"
                                onClick={handleSaveTask}
                                disabled={!isEditable}
                            >
                                Save
                            </button>
                            <button
                                className="btn cus-seconday-bg-btn top-btn"
                                onClick={handleResetForm}
                                disabled={!isEditable || formReset}
                            >
                                Reset Form
                            </button>
                            <button
                                className="btn cus-seconday-bg-btn top-btn"
                                onClick={handleEditRecord}
                                disabled={!formData.isEditable || isEditable}
                            >
                                Edit
                            </button>
                            <button
                                className="btn cus-primary-transparent-btn bckbtn"
                                onClick={() => navigate('/tasks')}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </div>
                {showLoader && <div className="full-screen-spinner-overlay zIndex2"><LoaderSpinner /></div>}
                <div className="incoming_wrapper main_content_table fontFix addressDetailWrapper">
                    <div className='row mb-3'>
                        <div className='col-lg-6 col-md-6 col-sm-12 mobMb-10'>
                            <div className="input-label-txt">Type <span className="require-field">*</span></div>
                            <select
                                className="form-control"
                                value={formData.taskTypeId}
                                name="taskTypeId"
                                onChange={handleInputChange}
                                disabled={!isEditable}
                            >
                                <option value="0">Select Task Type</option>
                                {allTaskTypeList?.map((item, index) => (
                                    <option key={item.id} value={item.id}>{item.typeName}</option>
                                ))}
                            </select>
                            {errors.taskTypeId && <div className="requiredthis">*This is required</div>}
                        </div>
                        <div className='col-lg-6 col-md-6 col-sm-12 mobMb-10'>
                            <div className="input-label-txt">Priority <span className="require-field">*</span></div>
                            <select
                                className="form-control"
                                value={formData.taskPriorityId}
                                name="taskPriorityId"
                                onChange={handleInputChange}
                                disabled={!isEditable}
                            >
                                <option value="0">Select Priority</option>
                                {allTaskPriorityList?.map((item, index) => (
                                    <option key={item.id} value={item.id}>{item.priorityName}</option>
                                ))}
                            </select>
                            {errors.taskPriorityId && <div className="requiredthis">*This is required</div>}
                        </div>
                    </div>
                    <div className='row mb-3'>
                        <div className={`col-lg-${isSuperAdminUser ? '6' : '12'} col-md-12 col-sm-12 mobMb-10`}>
                            <div className="input-label-txt">Title <span className="require-field">*</span></div>
                            <input
                                className="form-control"
                                placeholder="Enter task title"
                                type="text"
                                value={formData.taskTitle || ''}
                                name="taskTitle"
                                onChange={handleInputChange}
                                disabled={!isEditable}
                                onBlur={handleBlur}
                            />
                            {errors.taskTitle && <div className="requiredthis">*This is required</div>}
                        </div>
                        {isSuperAdminUser && (
                            <>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    <div className="input-label-txt">Company <span className="require-field">*</span></div>
                                    <div className="select_react">
                                        <Select
                                            options={companyList}
                                            onChange={(opt) => {
                                                const val = opt?.value || 0;
                                                setFormData(prev => ({ ...prev, companyId: val, sellerId: 0 }));
                                            }}
                                            className="activeSellerListInput"
                                            // Disable company select for existing tasks (shouldn't be editable)
                                            isDisabled={Number(id) > 0 || !isEditable}
                                            value={companyList?.find(c => c.value === formData.companyId) || null}
                                            placeholder="Select Company..."
                                            isMulti={false}
                                            classNamePrefix="list-value"
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    minHeight: '28px',
                                                    height: '36px',
                                                    fontSize: '13px',
                                                    paddingTop: '0.5px'
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    color: '#515558ff'
                                                })
                                            }}
                                        />
                                    </div>
                                    {errors.companyId && <div className="requiredthis">*This is required</div>}
                                </div>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    <div className="input-label-txt">Seller <span className="require-field">*</span></div>
                                    <div className="select_react">
                                        <Select
                                            options={sellerList}
                                            onChange={(opt) => setFormData(prev => ({ ...prev, sellerId: opt?.value || 0 }))}
                                            className="activeSellerListInput"
                                            // Disable seller select for existing tasks (shouldn't be editable)
                                            isDisabled={Number(id) > 0 || !isEditable}
                                            value={sellerList?.find(s => s.value === formData.sellerId) || null}
                                            placeholder="Select Seller..."
                                            isMulti={false}
                                            classNamePrefix="list-value"
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    minHeight: '28px',
                                                    height: '36px',
                                                    fontSize: '13px',
                                                    paddingTop: '0.5px'
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    color: '#515558ff'
                                                })
                                            }}
                                        />
                                    </div>
                                    {errors.sellerId && <div className="requiredthis">*This is required</div>}
                                </div>
                            </>
                        )}
                    </div>
                    <div className='row mb-3'>
                        <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                            <div className="input-label-txt">Status <span className="require-field">*</span></div>
                            <select
                                className="form-control"
                                value={formData.taskStatusId ?? 0}
                                name="taskStatusId"
                                onChange={handleInputChange}
                                disabled={!isSuperAdminUser || !isEditable}
                            >
                                {allTaskStatusList?.map((item) => (
                                    <option key={item.id} value={item.id}>{item.statusName}</option>
                                ))}
                            </select>
                            {errors.taskStatusId && <div className="requiredthis">*This is required</div>}
                        </div>
                        {isWalletRechargeTaskType ? (
                            <>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    <div className="input-label-txt">Currency <span className="require-field">*</span></div>
                                    <select
                                        className={`form-control ${errors.baseCurrencyId ? 'errorField' : ''}`}
                                        value={formData.baseCurrencyId || 1}
                                        name="baseCurrencyId"
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                    >
                                        {ledgerCurrencyList?.map((currency) => (
                                            <option key={currency.id} value={currency.id}>
                                                {currency.currencyName}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.baseCurrencyId && <div className="requiredthis">*This is required</div>}
                                </div>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    <div className="input-label-txt">Conversion Rate <span className="require-field">*</span></div>
                                    <input
                                        className={`form-control ${errors.conversionRate ? 'errorField' : ''}`}
                                        type="number"
                                        name="conversionRate"
                                        placeholder="Enter Conversion Rate"
                                        value={formData.conversionRate || ''}
                                        onChange={handleInputChange}
                                        disabled={!isEditable || Number(formData.baseCurrencyId) === 1}
                                        onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                        onScroll={(e) => e.target.blur()}
                                    />
                                    {errors.conversionRate && <div className="requiredthis">*This is required</div>}
                                </div>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    <div className="input-label-txt">Conversion Amount <span className="require-field">*</span></div>
                                    <input
                                        className={`form-control ${errors.baseConversionAmount ? 'errorField' : ''}`}
                                        type="number"
                                        name="baseConversionAmount"
                                        placeholder="Enter Conversion Amount"
                                        value={formData.baseConversionAmount || ''}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                        onScroll={(e) => e.target.blur()}
                                    />
                                    {renderConversionAmountDisplay()}
                                    {errors.baseConversionAmount && <div className="requiredthis">*This is required</div>}
                                </div>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    <div className="input-label-txt">Amount ($)</div>
                                    <div className="input-group taskTopUpInputGroup">
                                        <input
                                            className={`form-control`}
                                            type={"number"}
                                            name="amount"
                                            maxLength={10}
                                            placeholder="Enter Amount"
                                            value={formData.amount || ''}
                                            onChange={handleInputChange}
                                            disabled={!isEditable}
                                            readOnly={isWalletRechargeTaskType}
                                            onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                            onBlur={handleBlur}
                                            onScroll={(e) => e.target.blur()}
                                        />
                                        {Number(id) > 0 && isSuperAdminUser && (
                                            <button
                                                type="button"
                                                className="btn cus-seconday-bg-btn taskTopUpBtn"
                                                onClick={handleOpenTopUpModal}
                                                disabled={!formData.companyId || !formData.sellerId || !formData.amount}
                                                title="Top Up"
                                                aria-label="Top Up"
                                            >
                                                <img src={TopUpIconWhite} alt="" />
                                                <span>Top Up</span>
                                            </button>
                                        )}
                                    </div>
                                    {errors.amount && <div className="requiredthis">*This is required</div>}
                                </div>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    <div className="input-label-txt">Payment Mode <span className="require-field">*</span></div>
                                    <select
                                        className={`form-control ${errors.paymentModeId ? 'errorField' : ''}`}
                                        value={formData.paymentModeId || ''}
                                        name="paymentModeId"
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                    >
                                        <option value="">Select Payment Mode</option>
                                        {ledgerPaymentModeList?.map((paymentMode) => (
                                            <option key={paymentMode.id} value={paymentMode.id}>
                                                {paymentMode.paymentMode}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.paymentModeId && <div className="requiredthis">*This is required</div>}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    {formData?.taskCategory === 'ReturnPackage' ? (
                                        <>
                                            <div className="input-label-txt">Tracking Number</div>
                                            <input
                                                className={`form-control outboundNumber`}
                                                type="text"
                                                name="packageTrackingNumber"
                                                placeholder="Tracking Number"
                                                value={formData.packageTrackingNumber || ''}
                                                disabled
                                                maxLength={50}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <div className="input-label-txt">SKU</div>
                                            <input
                                                className={`form-control outboundNumber`}
                                                type="text"
                                                name="sku"
                                                placeholder="Enter SKU"
                                                value={formData.sku || ''}
                                                onChange={handleInputChange}
                                                disabled={!isEditable || formData?.taskCategory === 'ReturnProduct'}
                                                onBlur={handleBlur}
                                                maxLength={30}
                                            />
                                        </>
                                    )}
                                </div>
                            </>
                        )}
                        <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                            <div className="input-label-txt">Quantity</div>
                            <input
                                className={`form-control outboundNumber`}
                                type="number"
                                name="quantity"
                                placeholder="Enter Quantity"
                                value={formData.quantity || ''}
                                maxLength={10}
                                onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                onChange={handleInputChange}
                                disabled={!isEditable || formData?.taskCategory === 'ReturnProduct' || formData?.taskCategory === 'ReturnPackage'}
                                onBlur={handleBlur}
                            />
                        </div>
                        <div className="col-lg-3 col-md-6 col-sm-12 mobMb-10">
                            <div className="input-label-txt">Date</div>
                            <DatePickerComponent
                                dateField={formData.entryDate}
                                handleDateChange={(date) => handleDateFieldChange(date)}
                                fieldName="entryDate"
                                dateFormat="MM-dd-yyyy"
                                disabled={!isEditable || !isSuperAdminUser}
                                onKeyDown={(e) => {
                                    e.preventDefault();
                                }}
                            />
                        </div>
                    </div>
                    {isCommissionTaskType && (
                        <CommissionSection
                            formData={formData}
                            errors={errors}
                            isEditable={isEditable}
                            commissionAmount={commissionAmount}
                            onBaseChange={handleCommissionChange('commissionBaseAmount', isAllowedCommissionBaseInput)}
                            onRateChange={handleCommissionChange('commissionRate', isAllowedCommissionRateInput)}
                        />
                    )}
                    {showWorkmanSection && (
                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="workmanTimeLogCard">
                                    <div className="workmanTimeLogCard__header">
                                        <span className="workmanTimeLogCard__title">
                                            <i className="fa fa-clock-o" aria-hidden="true"></i>
                                            <strong className="ml-2">Workman time log</strong>
                                        </span>
                                        {showWorkmanActionButtons && (
                                            <div className="workmanTimeLogCard__actions">
                                                {!hasOpenWorkmanLog && (
                                                    <button
                                                        type="button"
                                                        className="btn cus-primary-bg-btn"
                                                        onClick={handleStartWorkmanTask}
                                                        disabled={!isEditable}
                                                    >
                                                        <i className="fa fa-play" aria-hidden="true"></i>{' '}
                                                        Start Task
                                                    </button>
                                                )}
                                                {hasOpenWorkmanLog && (
                                                    <button
                                                        type="button"
                                                        className="btn cus-seconday-bg-btn"
                                                        onClick={handleEndWorkmanTask}
                                                        disabled={!isEditable}
                                                    >
                                                        <i className="fa fa-stop" aria-hidden="true"></i>{' '}
                                                        End Task
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="workmanTimeLogCard__body">
                                        {(formData.workmanTimeLogs || []).length === 0 && isWarehouseUser && !isSuperAdminUser && (
                                            <div className="workmanTimeLogCard__empty">
                                                No time logs yet. Click <strong>Start Task</strong> to begin tracking time.
                                            </div>
                                        )}
                                        {(formData.workmanTimeLogs || []).map((log, idx) => {
                                            const rowErr = errors?.workmanTimeLogs?.[idx] || {};
                                            const rangeErr = getRowRangeError(log);
                                            const rowDurationLabel = computeDurationLabel(log?.startDateTime, log?.endDateTime);
                                            const canRemove = canEditWorkmanInputs && (formData.workmanTimeLogs || []).length > 1;
                                            return (
                                                <div className="workmanTimeLogCard__logRow" key={log?.id ?? `new-${idx}`}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="workmanTimeLogCard__logIndex">Log #{idx + 1}</span>
                                                        {canRemove && (
                                                            <button
                                                                type="button"
                                                                className="workmanTimeLogCard__removeBtn"
                                                                onClick={() => handleRemoveWorkmanLog(idx)}
                                                                aria-label={`Remove log ${idx + 1}`}
                                                            >
                                                                <i className="fa fa-trash" aria-hidden="true"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-lg-3 col-md-6 col-sm-12 mobMb-10">
                                                            <div className="input-label-txt">
                                                                Start datetime {isWorkmanLaborType && <span className="require-field">*</span>}
                                                            </div>
                                                            <DatePickerComponent
                                                                dateField={log?.startDateTime || null}
                                                                handleDateChange={(date) =>
                                                                    handleWorkmanLogChange(idx, 'startDateTime', date)
                                                                }
                                                                fieldName={`workmanLogStart_${idx}`}
                                                                dateFormat="MM-dd-yyyy HH:mm"
                                                                timeSelect={true}
                                                                disabled={!canEditWorkmanInputs}
                                                            />
                                                            {rowErr.startDateTime && (
                                                                <div className="requiredthis">*{rowErr.startDateTime}</div>
                                                            )}
                                                        </div>
                                                        <div className="col-lg-3 col-md-6 col-sm-12 mobMb-10">
                                                            <div className="input-label-txt">
                                                                End datetime {isWorkmanLaborType && <span className="require-field">*</span>}
                                                            </div>
                                                            <DatePickerComponent
                                                                dateField={log?.endDateTime || null}
                                                                handleDateChange={(date) =>
                                                                    handleWorkmanLogChange(idx, 'endDateTime', date)
                                                                }
                                                                fieldName={`workmanLogEnd_${idx}`}
                                                                dateFormat="MM-dd-yyyy HH:mm"
                                                                timeSelect={true}
                                                                disabled={!canEditWorkmanInputs}
                                                            />
                                                            {(rowErr.endDateTime || rangeErr) && (
                                                                <div className="requiredthis">
                                                                    *{rowErr.endDateTime || rangeErr}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="col-lg-3 col-md-6 col-sm-12 mobMb-10">
                                                            <div className="input-label-txt">Duration</div>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={rowDurationLabel}
                                                                readOnly
                                                                placeholder="0h 0m"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {canEditWorkmanInputs && (
                                            <div className="mb-3 d-flex align-items-center justify-content-between flex-wrap">
                                                <button
                                                    type="button"
                                                    className="workmanTimeLogCard__addBtn"
                                                    onClick={handleAddWorkmanLog}
                                                >
                                                    <i className="fa fa-plus" aria-hidden="true"></i>{' '}
                                                    Add another time log
                                                </button>
                                                {/* Save time logs button removed: time logs are persisted
                                                    via the dedicated Start/End Task buttons. Showing a
                                                    separate Save action caused duplicate rows to be
                                                    created when combined with those endpoints. */}
                                            </div>
                                        )}
                                        {(formData.workmanTimeLogs || []).length > 0 && (
                                            <div className="workmanTimeLogCard__summary">
                                                <div className="workmanTimeLogCard__summaryLeft">
                                                    <span className="workmanTimeLogCard__duration">
                                                        {formatDurationLabel(totalWorkmanMinutes)}
                                                    </span>
                                                    <span className="workmanTimeLogCard__totalLabel">
                                                        Total time spent
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        <div className="workmanTimeLogCard__footer">
                                            Duration is automatically calculated from start and end date &amp; time for each log; the total is summed across all logs.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className='row mb-3'>
                        <div className="col col-lg-6 col-md-6 col-sm-12 mobMb-10">
                            <div className="input-label-txt">Attachments <i id="uploadInfo" className="fa fa-info-circle" aria-hidden="true"></i></div>
                            <WmsTooltip
                                anchorId={`uploadInfo`}
                                content={`Upload files to attach to this task. You can upload multiple files here. Max file size is ${maxSize}MB.`}
                                place="top"
                                className="tooltipTxt"
                            />
                            <div className="uploadContainer">
                                <div className="inlineBlock fileUpload inventory">
                                    <button
                                        type="button"
                                        className="btn cus-seconday-bg-btn"
                                        disabled={!isEditable}
                                    >
                                        Upload documents
                                    </button>
                                    <input
                                        className="hiddenFileField"
                                        type="file"
                                        id="customFile"
                                        multiple
                                        onChange={handleAttachments}
                                        disabled={!isEditable}
                                    />
                                </div>
                                <button className='ml-2 inlineBlock fileUpload inventory link sortBtn' onClick={handleCancelPdfPopUp}>
                                    Upload & Merge Pdf Files <i id="uploadMerge" className="fa fa-info-circle" aria-hidden="true"></i>
                                </button>
                                <WmsTooltip
                                    anchorId={`uploadMerge`}
                                    content={`Upload PDFs to combine, download the merge file, then re-upload it here.`}
                                    place="top"
                                    className="tooltipTxt"
                                />
                                <div>
                                    {formData.attachments.filter(item => item.fileUrl).length > 0 &&
                                        <button className='btn mt-1 mr-2 downloadAll' onClick={handleDownloadAll}><i className="fa fa-download" aria-hidden="true"></i> Download All</button>
                                    }
                                </div>
                                <div className='displayFlex attachmentsFlex'>
                                    {formData.attachments.map((item, index) => (
                                        <Fragment key={item?.id}>
                                            {!item.isDeleted && item.fileUrl && (
                                                <div className={`selectedFile mr-2`} key={item?.id}>
                                                    <div className="fileName">
                                                        {/\.(jpg|jpeg|png|gif|webp)$/i.test(item.name) ? (
                                                            <button className='sortBtn imagePreview' onClick={() => handleViewImage(item)}>
                                                                <img
                                                                    src={item.fileUrl}
                                                                    alt={item.name}
                                                                    style={{ width: "100px", height: "auto", borderRadius: "8px" }}
                                                                />
                                                            </button>
                                                        ) : (
                                                            <a
                                                                href={item.fileUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                {item.name}
                                                            </a>
                                                        )}
                                                    </div>
                                                    {isEditable && (
                                                        <button
                                                            className="deleteFile"
                                                            onClick={() =>
                                                                handleDeleteAttachment(item, index)
                                                            }
                                                            disabled={!isEditable}
                                                        >
                                                            <i className="fa fa-times" aria-hidden="true"></i>
                                                        </button>
                                                    )}
                                                    <button
                                                        className="downBtn ml-2"
                                                        onClick={() =>
                                                            handleDownload(item)
                                                        }
                                                    >
                                                        <i className="fa fa-download" aria-hidden="true"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </Fragment>
                                    ))}
                                </div>
                                <div className='displayFlex alignItemsCenter flexWrap wrapTasks'>
                                    {formData.attachments.map((item, index) => (
                                        <Fragment key={item?.id}>
                                            {!item.fileUrl && (
                                                <div className={`selectedFile mr-2 filePreview`} key={item?.id}>
                                                    <div className="fileName">
                                                        <div>
                                                            {item.name}
                                                            <InlineFilePreview
                                                                file={item}
                                                                onClose={() => handleDeleteAttachment(item, index)}
                                                            />
                                                        </div>
                                                    </div>
                                                    {isEditable && (
                                                        <button
                                                            className="deleteFile"
                                                            onClick={() => handleDeleteAttachment(item, index)}
                                                            type="button"
                                                        >
                                                            <i className="fa fa-times" aria-hidden="true"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </Fragment>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='row mb-3'>
                        <div className='col-lg-12 col-md-12 col-sm-12 mobMb-10'>
                            <div className="input-label-txt" htmlFor="instructions">Comment</div>
                            <textarea
                                onBlur={handleBlur}
                                className="form-control"
                                placeholder="Enter any comment"
                                rows="7"
                                value={formData?.instructions}
                                name="instructions"
                                id="instructions"
                                onChange={handleInputChange}
                                disabled={!isEditable}
                            ></textarea>
                        </div>
                    </div>
                    {isDefaultSuperAdminUser && (
                        <div className='row mb-3'>
                            <div className='col-lg-9 col-md-12 col-sm-12 mobMb-10'>
                                <div className="input-label-txt" htmlFor="internalNotes">Internal Notes</div>
                                <textarea
                                    onBlur={handleBlur}
                                    className="form-control"
                                    placeholder="Enter internal notes (visible to Super Admin only)"
                                    rows="4"
                                    value={formData?.internalNotes ?? ''}
                                    name="internalNotes"
                                    id="internalNotes"
                                    onChange={handleInputChange}
                                    disabled={!isEditable}
                                ></textarea>
                            </div>
                            <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                <div className="input-label-txt" htmlFor="actualCost">Actual Cost ($)</div>
                                <input
                                    className="form-control"
                                    type="text"
                                    inputMode="decimal"
                                    name="actualCost"
                                    id="actualCost"
                                    placeholder="Enter actual cost"
                                    maxLength={15}
                                    value={formData?.actualCost ?? ''}
                                    onChange={handleActualCostChange}
                                    disabled={!isEditable}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showCharge && (
                <ChargeSeller show={showCharge} taskId={id} handleCancel={() => setShowCharge(false)} fetchTaskDetails={fetchTaskDetails} defaultAmount={getChargeDefaultAmount(isCommissionTaskType, commissionAmount)} />
            )}
            {renderTaskTopUpModal()}
            {showPopUp && (
                <PdfMergePopup handleCancel={handleCancelPdfPopUp} showPopUp={showPopUp} handleUploadPdfAction={handleUploadPdfAction} />
            )}

            {showImageModal && (
                <ViewImage
                    selectedRecord={selectedImageRecord}
                    showHideImageModal={showImageModal}
                    setShowHideImageModal={setShowImageModal}
                    title="Preview"
                />
            )}

            {showChargesDetails && (
                <ChargesHistory
                    show={showChargesDetails}
                    handleCancel={() => setShowChargesDetails(false)}
                    data={formData}
                />
            )}
            <Tooltip anchorId="chargesBtn" place="right" className="tooltipWidth fontFix">
                <div>Show Charges History</div>
            </Tooltip>

            <AlertDialogModal
                modalShow={showChargeAlert}
                handleCancelDeleteAlert={() => setShowChargeAlert(false)}
                modalClass={"modalDeleteWrapper"}
                messageText="This task has already been charged. Do you want to charge it again?"
                handleYesDeleteAlert={handleOverrideCharge}
                headingText={"POST CHARGES"}
            />
        </main >
    );
}

export default Tasks;
